import { useEffect, useState, useCallback } from 'react';
import axiosInstance from '../api/axios';
import { API_ENDPOINTS } from '../api/endpoints';

/** The free-trial offer granted when a provider creates a business. */
export interface TrialSettings {
  is_enabled: boolean;
  trial_days: number;
  provider_count: number;
  token_count: number;
  updated_at: string | null;
}

export type TrialSettingsPatch = Partial<Omit<TrialSettings, 'updated_at'>>;

export function useTrialSettings() {
  const [settings, setSettings] = useState<TrialSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.TRIAL_SETTINGS);
      setSettings(res.data?.data ?? res.data);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load trial settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  /** Saves the changed fields. Resolves to an error message, or null on success. */
  const save = useCallback(async (patch: TrialSettingsPatch): Promise<string | null> => {
    setSaving(true);
    try {
      const res = await axiosInstance.put(API_ENDPOINTS.TRIAL_SETTINGS, patch);
      setSettings(res.data?.data ?? res.data);
      return null;
    } catch (err: any) {
      return err?.response?.data?.message || 'Failed to save trial settings.';
    } finally {
      setSaving(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  return { settings, loading, saving, error, reload: load, save };
}
